// ================================================================================================
// React components
import { Component } from "react";
import _ from "lodash";
import { SimpleEventDispatcher } from "strongly-typed-events";

import FontIcon from 'material-ui/FontIcon';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';

import type { MacroUserValidationFormInfo } from './BoardMacroEngine';
import { BoardTheming } from './BoardTheming';
import { getZDepthShadow } from '@Utils/Colors';
import { getRandomKey, IsNullOrWhiteSpace } from '@Utils/Micro';
import { Button } from '@Primitive/Button';
import { Markdown } from '@Primitive/Markdown/MarkdownLazy';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';

// ================================================================================================

export interface MacroUserValidationFormProp {
    width: number;
    height: number;
    info: MacroUserValidationFormInfo;
    resultCB: (accepted: boolean, formData: any) => void;
    style?: {
        bodyBackgroundColor?: string;
        toolbarBackgroundColor?: string;
        toolbarFontColor?: string;
        toolbarShadowLevel?: number;
        acceptBackgroundColor?: string;
        acceptFontColor?: string;
        cancelBackgroundColor?: string;
        cancelFontColor?: string;
    }
}

// ------------------------------------------------------------------------------------------------

export interface MacroUserValidationFormState {

}

// ------------------------------------------------------------------------------------------------

export default class MacroUserValidationForm extends Component<MacroUserValidationFormProp, MacroUserValidationFormState> {
    constructor(props: MacroUserValidationFormProp) {
        super(props);
        this.state = {

        }
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const theme = BoardTheming.GetActiveTheme();
        const info = this.props.info;
        const style = this.props.style;
        const toolbarHeight = theme.dialog.height;
        const iconSize = info.IconSize;
        const hasIcon = !IsNullOrWhiteSpace(info.Icon);
        const buttonsHeight = 60;

        return <div style={{
            width: this.props.width,
            height: this.props.height,
            boxShadow: getZDepthShadow(1),
            borderRadius: 10,
            overflow: "hidden"
        }}>
            <Toolbar key="title-toolbar" style={{
                height: toolbarHeight,
                boxShadow: getZDepthShadow(style?.toolbarShadowLevel ?? theme.gemUserDialogBox.toolbarShadowLevel), position: "relative",
                color: style?.toolbarFontColor ?? theme.gemUserDialogBox.toolbarFontColor,
                backgroundColor: style?.toolbarBackgroundColor ?? theme.gemUserDialogBox.toolbarBackgroundColor,
                zIndex: 1000
            }} >
                <ToolbarGroup>
                    <ToolbarTitle style={{ color: style?.toolbarFontColor ?? theme.gemUserDialogBox.toolbarFontColor }} key="title-toolbar-title" text={info.Title} />
                </ToolbarGroup>
                <ToolbarGroup style={{ color: style?.toolbarFontColor ?? theme.gemUserDialogBox.toolbarFontColor }} lastChild={true}>
                    <div style={{
                        boxSizing: 'border-box',
                        textAlign: 'right',
                        display: "table",
                        marginRight: 10
                    }}>
                    </div>
                </ToolbarGroup>
            </Toolbar>
            <div style={{
                width: this.props.width,
                height: this.props.height - toolbarHeight - buttonsHeight,
                backgroundColor: style?.bodyBackgroundColor ?? theme.gemUserDialogBox.bodyBackgroundColor,
                overflow: "auto",
                fontSize: 16,
                padding: 10,
                fontWeight: 500, boxSizing: "border-box", position: "relative", whiteSpace: "nowrap",
                display: "flex"
            }}>
                {
                    (hasIcon) ?
                        <FontIcon
                            style={{
                                width: iconSize, height: iconSize, fontSize: iconSize, textAlign: "center",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                margin: 14,
                                color: info.IconColor
                            }}
                            className={info.Icon}
                        /> : null
                }

                <Markdown
                    key="message-body"
                    source={info.Body}
                    style={{ display: "inline-block", verticalAlign: "top", whiteSpace: "normal", flex: 1 }}
                />
            </div>
            <div style={{
                height: buttonsHeight,
                width: this.props.width,
                padding: 10,
                backgroundColor: style?.bodyBackgroundColor ?? theme.gemUserDialogBox.bodyBackgroundColor,
                display: "flex", flexDirection: "row",
                alignItems: "center", justifyContent: "flex-end"
            }}>

                {
                    (info.ShowCancelButton) ?
                        <Button
                            key="button-cancel"
                            label={info.CancelLabel}
                            onClick={() => this.props.resultCB(false, null)}
                            borderRadius={10}
                            shadowLevel={1}
                            style={{
                                backgroundColor: style?.cancelBackgroundColor ?? theme.gemUserDialogBox.cancelBackgroundColor,
                                color: style?.cancelFontColor ?? theme.gemUserDialogBox.cancelFontColor,
                                height: Math.ceil(buttonsHeight * 0.7),
                                margin: 10
                            }}
                        /> : null
                }

                <Button
                    key="button-accept"
                    label={info.AcceptLabel}
                    onClick={() => this.props.resultCB(true, null)}
                    borderRadius={10}
                    shadowLevel={1}
                    style={{
                        backgroundColor: style?.acceptBackgroundColor ?? theme.gemUserDialogBox.acceptBackgroundColor,
                        color: style?.acceptFontColor ?? theme.gemUserDialogBox.acceptFontColor,
                        height: Math.ceil(buttonsHeight * 0.7),
                        margin: 10
                    }}
                />
            </div>
        </div>
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================

export interface DialogConfExt extends DialogConf {
    key: string;
}

export interface MacroUserValidationFormManagerReq {
    info: MacroUserValidationFormInfo;
    resultCB: (accepted: boolean, formData: any) => void;

}

// ------------------------------------------------------------------------------------------------

export interface MacroUserValidationFormManagerProps {
}

// ------------------------------------------------------------------------------------------------

export interface MacroUserValidationFormManagerState {
    dialogs: DialogConfExt[];
}

// ------------------------------------------------------------------------------------------------

export class MacroUserValidationFormManager extends Component<MacroUserValidationFormManagerProps, MacroUserValidationFormManagerState> {
    private static onUserValidationRequest: SimpleEventDispatcher<MacroUserValidationFormManagerReq>;
    constructor(props: MacroUserValidationFormManagerProps) {
        super(props);
        this.state = {
            dialogs: []
        }

        if (MacroUserValidationFormManager.onUserValidationRequest == null)
            MacroUserValidationFormManager.onUserValidationRequest = new SimpleEventDispatcher();

        // Register to get the requests
        MacroUserValidationFormManager.onUserValidationRequest.subscribe(this.onUserValidationRequestCB);
    }

    // --------------------------------------------------------------------------------------------

    onUserValidationRequestCB = (req: MacroUserValidationFormManagerReq) => {
        this.setState(s => {
            let key = getRandomKey();
            s.dialogs.push({
                key: key,
                contentStaticSize: { w: req.info.SizeWidth / 100, h: req.info.SizeHeight / 100 },
                bodyFunc: (w, h) => <MacroUserValidationForm
                    info={req.info}
                    height={h}
                    width={w}
                    resultCB={(accepted: boolean, formData: any) => {
                        // Send to caller the results
                        req.resultCB(accepted, formData);

                        // Close the dialog
                        this.setState(sx => {
                            _.remove(sx.dialogs, d => d.key == key);
                            return sx;
                        })
                    }}
                />
            })
            return s;
        });
    }

    // --------------------------------------------------------------------------------------------

    render() {
        return _.map(this.state.dialogs, d =>
            <ExDialog
                Enable={true}
                Conf={d}
                style={{
                    backgroundColor: "transparent"
                }}
            />);
    }

    // --------------------------------------------------------------------------------------------

    public static AddUserValidationDialog(req: MacroUserValidationFormManagerReq) {
        this.onUserValidationRequest.dispatchAsync(req);
    }
}

// ================================================================================================